import type { Color } from "@asap/design/colors";
import type {
  CreateWantedProfileBody,
  WantedProfile,
  WantedProfileListParams,
} from "@asap/shared/schemas/wanted-profile";
import {
  EmployeeStatusSchema,
  WantedProfileStatusSchema,
  type EmployeeStatus,
  type WantedProfileStatus,
} from "@asap/shared/types/supabase.types.gen";
import { translateEmployeeStatus } from "@asap/shared/utils/translate";
import type { TableEmptyStateType } from "~/utils/types";

export type WantedProfileTableFilters = {
  search?: string;
  agencyId?: string;
  employeeStatus?: EmployeeStatus;
  status?: WantedProfileStatus;
  pageSize: number;
  pageIndex: number;
};

export type WantedProfileForm = {
  agencyId?: string;
  jobCode?: string;
  employeeStatus?: EmployeeStatus;
  description: string;
};

const wantedProfileEmployeeStatuses = EmployeeStatusSchema.options.map((option) => option.value);

export const wantedProfileQualificationItems = wantedProfileEmployeeStatuses.map((employeeStatus) => ({
  value: employeeStatus,
  label: translateEmployeeStatus.fr[employeeStatus],
}));

export const wantedProfileStatuses = WantedProfileStatusSchema.options
  .map((option) => option.value)
  .filter((status) => status !== "archived");

const statusColors: Record<WantedProfileStatus, Color> = {
  open: "warning",
  covered: "success",
  archived: "secondary",
};

export const getWantedProfileStatusColor = (status: WantedProfileStatus): Color => statusColors[status];

export const getStatusAfterToggle = (status: WantedProfileStatus): "open" | "covered" =>
  status === "open" ? "covered" : "open";

export const getLastPageIndex = (totalItems: number, pageSize: number) =>
  Math.max(0, Math.ceil(totalItems / pageSize) - 1);

const hasActiveWantedProfileFilters = (filters: WantedProfileTableFilters) =>
  Boolean(filters.search || filters.agencyId || filters.employeeStatus || filters.status);

export const getWantedProfileEmptyStateType = (filters: WantedProfileTableFilters): TableEmptyStateType =>
  hasActiveWantedProfileFilters(filters) ? "no-data-with-filters" : "no-data";

export const buildWantedProfileListQuery = (
  companyId: string,
  filters: WantedProfileTableFilters
): WantedProfileListParams => ({
  company_id: companyId,
  agency_id: filters.agencyId,
  employee_status: filters.employeeStatus,
  search: filters.search || undefined,
  status: filters.status,
  pageIndex: filters.pageIndex,
  pageSize: filters.pageSize,
});

export const buildWantedProfileForm = (wantedProfile?: WantedProfile): WantedProfileForm => ({
  agencyId: wantedProfile?.agency.id,
  jobCode: wantedProfile?.job.code,
  employeeStatus: wantedProfile?.employee_status ?? undefined,
  description: wantedProfile?.description ?? "",
});

export const buildWantedProfileBody = (form: WantedProfileForm): CreateWantedProfileBody | undefined => {
  if (!form.agencyId || !form.jobCode || !form.employeeStatus) return undefined;
  return {
    agency_id: form.agencyId,
    job_matrix_code: form.jobCode,
    employee_status: form.employeeStatus,
    description: form.description.trim() || null,
  };
};

export const getAlreadyAddedJobCodes = (
  jobCodesOfAgency: string[],
  agencyId: string,
  wantedProfileBeingEdited?: WantedProfile
): ReadonlySet<string> => {
  const ownJobCode = agencyId === wantedProfileBeingEdited?.agency.id ? wantedProfileBeingEdited.job.code : undefined;
  return new Set(jobCodesOfAgency.filter((jobCode) => jobCode !== ownJobCode));
};

export const keepJobCodeIfAvailable = (jobCode: string | undefined, alreadyAddedJobCodes: ReadonlySet<string>) =>
  jobCode && alreadyAddedJobCodes.has(jobCode) ? undefined : jobCode;

type JobItem = { value: string; label: string; disabled: boolean; isAlreadyAdded: boolean };

export const buildJobItems = (
  tags: { code: string; label: string }[],
  alreadyAddedCodes: ReadonlySet<string>,
  editedJob?: { code: string; label: string }
): JobItem[] => {
  const toJobItem = (job: { code: string; label: string }): JobItem => {
    const isAlreadyAdded = alreadyAddedCodes.has(job.code);
    return { value: job.code, label: job.label, disabled: isAlreadyAdded, isAlreadyAdded };
  };
  const items = tags.map(toJobItem);
  const isEditedJobListed = items.some((item) => item.value === editedJob?.code);
  if (!editedJob || isEditedJobListed) return items;
  return [toJobItem(editedJob), ...items];
};
